import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { LockKey, UploadSimple, ArrowsClockwise, LockKeyOpen } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const TONE = { draft: "bg-slate-100 text-slate-600", submitted: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700" };

export default function ReconSessionPanel({ bankCode, period, canEdit, onChanged }) {
  const [session, setSession] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [bal, setBal] = useState({ statement_opening: "", statement_closing: "" });
  const [csvText, setCsvText] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/finance/gl/bank-recon/sessions", { params: { bank_code: bankCode, period } });
      setSession((data.items || []).find((s) => s.status !== "rejected") || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [bankCode, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, body) => {
    try {
      const { data } = await api.post(path, body || {});
      if (data.note) toast.success(data.note); else toast.success("Done");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createSession = () => act("/finance/gl/bank-recon/sessions", {
    bank_code: bankCode, period,
    statement_opening: parseFloat(bal.statement_opening) || 0,
    statement_closing: parseFloat(bal.statement_closing) || 0,
  }).then(() => setNewOpen(false));
  const reopen = async () => {
    const reason = window.prompt("Reason to reopen this approved reconciliation:");
    if (!reason) return;
    await act(`/finance/gl/bank-recon/sessions/${session.id}/reopen`, { reason });
  };
  const importCsv = async () => {
    try {
      const { data } = await api.post("/finance/gl/bank-recon/import-csv", { bank_code: bankCode, csv_text: csvText, has_header: true });
      toast.success(`${data.imported} imported · ${data.duplicates_skipped} duplicate(s) skipped${data.errors.length ? ` · ${data.errors.length} error(s)` : ""}`);
      setCsvOpen(false); setCsvText(""); onChanged?.(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="dc-card px-5 py-3" data-testid="recon-session-panel">
      <div className="flex flex-wrap items-center gap-3">
        <LockKey size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <div className="font-display text-[15px] font-semibold text-slate-900">Reconciliation Sign-off · {period}</div>
        {session ? (
          <>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${TONE[session.status]}`} data-testid="recon-session-status">
              {session.status}{session.status === "approved" && " · LOCKED"}
            </span>
            <span className="font-mono-alt text-[11px] tabular-nums text-slate-600" data-testid="recon-session-figures">
              Book close {formatCurrency(session.book_closing)} · Stmt close {formatCurrency(session.statement_closing)} ·
              Δ <b className={Math.abs(session.difference) <= 0.01 ? "text-emerald-600" : "text-rose-600"}>{formatCurrency(session.difference)}</b> ·
              {session.statement_matched}/{session.statement_lines} matched
            </span>
            <span className="text-[10px] text-slate-400">prepared by {session.prepared_by}{session.status === "approved" && session.reviewed_by && ` · approved by ${session.reviewed_by}`}</span>
          </>
        ) : (
          <span className="text-xs text-slate-500">No reconciliation session for this bank & period yet.</span>
        )}
        <div className="ml-auto flex gap-2">
          {canEdit && <Button size="sm" variant="outline" className="h-7" onClick={() => setCsvOpen(true)} data-testid="recon-csv-btn"><UploadSimple size={12} className="mr-1" /> Import CSV</Button>}
          {canEdit && !session && (
            <Button size="sm" className="btn-primary-blue h-7" onClick={() => setNewOpen(true)} data-testid="recon-session-new-btn">Start session</Button>
          )}
          {canEdit && session && session.status === "draft" && (
            <>
              <Button size="sm" variant="outline" className="h-7" onClick={() => act(`/finance/gl/bank-recon/sessions/${session.id}/refresh`)} data-testid="recon-session-refresh"><ArrowsClockwise size={12} /></Button>
              <Button size="sm" className="btn-primary-blue h-7" onClick={() => act(`/finance/gl/bank-recon/sessions/${session.id}/submit`)} data-testid="recon-session-submit">Submit for review</Button>
            </>
          )}
          {canEdit && session && session.status === "submitted" && (
            <Button size="sm" className="btn-primary-blue h-7" onClick={() => act(`/finance/gl/bank-recon/sessions/${session.id}/approve`)} data-testid="recon-session-approve">Approve & lock</Button>
          )}
          {canEdit && session && session.status === "approved" && (
            <Button size="sm" variant="outline" className="h-7" onClick={reopen} data-testid="recon-session-reopen"><LockKeyOpen size={12} className="mr-1" /> Reopen</Button>
          )}
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md" data-testid="recon-session-dialog">
          <DialogHeader><DialogTitle>Start reconciliation session · {period}</DialogTitle>
            <DialogDescription>Enter the balances from the bank statement. Book balances come from the GL automatically.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Statement opening ($)</Label><Input type="number" step="0.01" value={bal.statement_opening} onChange={(e) => setBal({ ...bal, statement_opening: e.target.value })} data-testid="recon-stmt-opening" /></div>
            <div><Label>Statement closing ($)</Label><Input type="number" step="0.01" value={bal.statement_closing} onChange={(e) => setBal({ ...bal, statement_closing: e.target.value })} data-testid="recon-stmt-closing" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button className="btn-primary-blue" onClick={createSession} data-testid="recon-session-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="max-w-lg" data-testid="recon-csv-dialog">
          <DialogHeader><DialogTitle>Import bank statement CSV</DialogTitle>
            <DialogDescription>Header row with date, description, amount (or debit/credit) and optional ref. Duplicates are skipped automatically.</DialogDescription></DialogHeader>
          <Textarea rows={8} value={csvText} onChange={(e) => setCsvText(e.target.value)}
            placeholder={"date,description,amount,ref\n2026-07-01,Client transfer ACME,5000,TT-1041\n2026-07-03,Bank charges,-25.50,"}
            data-testid="recon-csv-text" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCsvOpen(false)}>Cancel</Button>
            <Button className="btn-primary-blue" onClick={importCsv} disabled={!csvText.trim()} data-testid="recon-csv-save">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
